import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
	FormItem,
	Button,
	Space,
	Modal,
	ModalTitle,
	Textbox,
	DayPickerInput,
	Spinner,
	RecorderBlock,
} from '@components';
import './EditMeetingRecSettingsModal.scss';
import { makeTime } from '@common';
import moment from 'moment';
import { editMeetingRecSettingsValidator, ValMeetingInfo } from '@modals';
import { apiInstance, MeetingRecordingSettings, RecordingAgentReg } from '@api';

interface EditMeetingRecSettingsModalProps {
	closeModal: () => void,
	meetingId: string,
	onSubmit: (editedMeetingInfo: MeetingRecordingSettings) => Promise<void>,
}

function EditMeetingRecSettingsModal(props: EditMeetingRecSettingsModalProps): JSX.Element {
	const {
		onSubmit,
		closeModal,
		meetingId,
	} = props;

	const [meetingSubject, setMeetingSubject] = useState<string>('');
	const [valMessages, setValMessages] = useState({});
	const [isUploading, setIsUploading] = useState(false);
	
	const [meetingLocation, setMeetingLocation] = useState<string>('');
	const [meetingStartDate, setMeetingStartDate] = useState(new Date());
	const [meetingStartTime, setMeetingStartTime] = useState(`${new Date().getHours()}:00`);
	const [meetingEndTime, setMeetingEndTime] = useState(`${new Date().getHours()}:30`);
	const [meetingAgent, setMeetingAgent] = useState<RecordingAgentReg>(new RecordingAgentReg());

	useEffect(() => {
		async function fetchData() {
			const meetingInfo = await apiInstance.getMeetingRecordingInfo(meetingId);

			setMeetingSubject(meetingInfo.subject);
			const startDate = moment(meetingInfo.startUtc).local();
			const endDate = moment(meetingInfo.endUtc).local();

			setMeetingStartDate(startDate.toDate());
			setMeetingStartTime(makeTime(startDate));
			setMeetingEndTime(makeTime(endDate));

			const newAgent = new RecordingAgentReg({
				id: meetingInfo.plannedRecAgentId,
				name: meetingInfo.plannedRecAgentName,
				configuration: meetingInfo.plannedRecAgentConfig,
			});

			setMeetingAgent(newAgent);
			setMeetingLocation(newAgent.name);
		}

		fetchData();
	}, [meetingId, setMeetingAgent]);

	const getFormData = useCallback(() => {
		const newMeetingInfo = new ValMeetingInfo();
		newMeetingInfo.id = meetingId;
		newMeetingInfo.subject = meetingSubject;

		newMeetingInfo.isPlannedRecording = true;
		newMeetingInfo.location = meetingLocation;

		newMeetingInfo.startDateString = meetingStartDate;
		newMeetingInfo.startTimeString = meetingStartTime;
		newMeetingInfo.endTimeString = meetingEndTime;
		newMeetingInfo.startAndEndTimes = [meetingStartTime, meetingEndTime, meetingStartDate];

		newMeetingInfo.plannedAgentRegId = meetingAgent && meetingAgent.id;
		newMeetingInfo.plannedAgentSeetings = meetingAgent && meetingAgent.configuration;

		return newMeetingInfo;

	}, [meetingSubject, meetingLocation,
		meetingStartDate, meetingStartTime, meetingEndTime,
		meetingAgent, meetingId]);

	const validateForm = useCallback((formData): boolean => {
		let result = false;

		const callback = (errors, fields) => {
			if (errors) {
				setValMessages(fields);
				result = false;
			} else {
				setValMessages({});
				result = true;
			}
		};

		editMeetingRecSettingsValidator.validate(formData, {}, callback);

		return result;
	}, [setValMessages]);

	const handleSubmit = useCallback(async (): Promise<void> => {
		const formData = getFormData();
		const valResult = validateForm(formData);

		if (valResult) {
			const newMeetingInfo = new MeetingRecordingSettings();
			newMeetingInfo.id = formData.id;
			newMeetingInfo.subject = formData.subject;

			const startHours = formData.startTimeString.split(':')[0];
			const startMinutes = formData.startTimeString.split(':')[1];

			const endHours = formData.endTimeString.split(':')[0];
			const endMinutes = formData.endTimeString.split(':')[1];

			newMeetingInfo.startUtc = moment(formData.startDateString)
				.startOf('day')
				.add(startHours, 'hours')
				.add(startMinutes, 'minutes')
				.utc()
				.toDate();

			newMeetingInfo.endUtc = moment(formData.startDateString)
				.startOf('day')
				.add(endHours, 'hours')
				.add(endMinutes, 'minutes')
				.utc()
				.toDate();

			newMeetingInfo.plannedRecAgentId = formData.plannedAgentRegId;
			newMeetingInfo.plannedRecAgentConfig = formData.plannedAgentSeetings;
			
			setIsUploading(true);
			await onSubmit(newMeetingInfo);
			closeModal();
		}

	}, [closeModal, onSubmit, validateForm, getFormData]);

	const headerView = useMemo((): JSX.Element => {
		return (
			<div className="create-meeting-modal__header">
				<ModalTitle onClose={closeModal}>
					Настройки собрания
					{isUploading && <Spinner />}
				</ModalTitle>
			</div>
		);
	}, [closeModal, isUploading]);

	const footerView = useMemo(() => {
		return (
		<Space>
			<Button onClick={closeModal}>
				Отменить
			</Button>
			<Button theme="primary" onClick={handleSubmit} disabled={isUploading}>
				Сохранить
			</Button>
			<div></div>
		</Space>
		);
	}, [handleSubmit, closeModal, isUploading]);

	const handleBlur = useCallback((): void => {
		const formData = getFormData();
		validateForm(formData);
	}, [getFormData, validateForm]);

	const onAgentChanged = useCallback((newAgent: RecordingAgentReg) => {
		setMeetingAgent(newAgent);
		setMeetingLocation(newAgent.name);
	}, [setMeetingAgent, setMeetingLocation]);

	return (
		<Modal
			className="create-meeting-modal"
			header={headerView}
			footer={footerView}
			isOpen
		>
			<div className="create-meeting-modal__content">
				<FormItem label="Название" validationMessages={valMessages['subject']}>
					<Textbox
						value={meetingSubject}
						onValueChange={setMeetingSubject}
						onBlur={handleBlur}
						disabled={isUploading}
					/>
				</FormItem>
				<Space>
					<FormItem label="Дата проведения" validationMessages={valMessages['startDateString']}>
						<DayPickerInput
							value={meetingStartDate}
							onBlur={handleBlur}
							onDayChange={setMeetingStartDate}
							disabled={isUploading}
						/>
					</FormItem>
					{/* {endTimeString} */}
					<FormItem label="Время проведения" validationMessages={valMessages['startAndEndTimes']}> 
						<Space>
							<Textbox
								type='time'
								value={meetingStartTime}
								onValueChange={setMeetingStartTime}
								className="time"
								onBlur={handleBlur}
								disabled={isUploading}
							/>
							<div className="time-interval">—</div>
							<Textbox
								type='time'
								value={meetingEndTime}
								onValueChange={setMeetingEndTime}
								className="time"
								onBlur={handleBlur}
								disabled={isUploading}
							/>
						</Space>
					</FormItem>
				</Space>
				<RecorderBlock 
					selectedAgent={meetingAgent}
					onAgentChanged={onAgentChanged}
				/>
			</div>
		</Modal>
	);
}

export {EditMeetingRecSettingsModal};

